import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { AlertCircle, Clock, Loader2, Download } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const money = (v) => `£${Number(v || 0).toFixed(2)}`;
const ukDate = (v) => {
  if (!v) return "";
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v).slice(0, 10);
};

const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAF9F6] py-10 px-4 print:bg-white print:py-0" data-testid={testid}>
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 print:border-0 print:rounded-none">
      {children}
    </div>
  </div>
);

const Status = ({ icon: Icon, title, body, testid, tone }) => (
  <Shell testid={testid}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${tone}`}>
      <Icon className="w-6 h-6" />
    </div>
    <h1 className="text-xl md:text-2xl text-[#1A1B1A] mb-3" style={H}>{title}</h1>
    <p className="text-sm text-[#4A4B4A] leading-relaxed">{body}</p>
  </Shell>
);

export default function PublicDocumentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [state, setState] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfError("");
    try {
      const res = await publicApi.get(`/public/documents/${token}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      if (!blob.size) throw new Error("empty pdf");
      const name = (res.headers?.["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1]
        || "document.pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      if ("download" in a) {
        a.href = url;
        a.download = name;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setPdfError("PDF could not be generated. Please try again or contact the clinic.");
    }
    setPdfBusy(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicApi.get(`/public/documents/${token}`);
        if (cancelled) return;
        setData(res.data);
        setState(res.data.status);
      } catch {
        if (!cancelled) setState("error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <Shell testid="public-doc-loading">
        <div className="flex items-center gap-3 text-sm text-[#757675]">
          <Loader2 className="w-5 h-5 animate-spin text-[#205B5A]" /> Loading your document...
        </div>
      </Shell>
    );
  }

  if (state === "expired") {
    return <Status icon={Clock} testid="public-doc-expired" tone="bg-amber-50 text-amber-600"
      title="This link has expired"
      body="For your security these links expire after a while. Please contact your clinic for a new one." />;
  }
  if (state === "not_ready") {
    return <Status icon={Clock} testid="public-doc-not-ready" tone="bg-amber-50 text-amber-600"
      title="This record isn't ready yet"
      body="Your clinician is still finishing this record. Please check back shortly, or contact the clinic if you need it urgently." />;
  }
  if (state !== "valid") {
    return <Status icon={AlertCircle} testid="public-doc-invalid" tone="bg-red-50 text-red-500"
      title="This link isn't valid"
      body="The link may have been mistyped or replaced by a newer one. Please contact your clinic." />;
  }

  const clinic = data.clinic || {};
  const isInvoice = data.doc_type === "invoice";
  const inv = data.invoice || {};
  const note = data.note || {};
  const outstanding = Math.max(Number(inv.total || 0) - Number(inv.paid_amount || 0), 0);

  return (
    <Shell testid="public-doc-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs text-[#757675]">{clinic.name}</p>
          <h1 className="text-2xl text-[#1A1B1A] mt-1" style={H}>
            {isInvoice ? `Invoice ${inv.invoice_number || ""}` : note.title}
          </h1>
          <p className="text-xs text-[#757675] mt-1">
            {data.patient_name} · {ukDate(isInvoice ? inv.date : note.date)}
          </p>
        </div>
        <div className="print:hidden w-full sm:w-auto flex-shrink-0">
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="download-document-pdf-btn"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 rounded-xl bg-[#205B5A] text-white text-sm sm:text-xs font-medium hover:bg-[#174544] disabled:opacity-60 transition-colors">
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {pdfBusy ? "Preparing PDF..." : "Download PDF"}
          </button>
          <p className="text-[10px] text-[#9A9B9A] mt-1.5 text-center sm:text-right">You can print it after downloading.</p>
        </div>
      </div>

      {pdfError && (
        <div className="mb-6 rounded-xl bg-red-50 border border-red-100 px-4 py-3 print:hidden" data-testid="download-pdf-error">
          <p className="text-xs text-red-600">{pdfError}</p>
        </div>
      )}

      {isInvoice ? (
        <div data-testid="public-invoice-body">
          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#757675] border-b border-[#1A1B1A]/[0.06]">
                <th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-b border-[#1A1B1A]/[0.03]">
                  <td className="py-2.5 text-[#1A1B1A]">{it.description || it.name}</td>
                  <td className="py-2.5 text-right text-[#757675]">{it.quantity ?? 1}</td>
                  <td className="py-2.5 text-right text-[#1A1B1A]">{money(it.total ?? it.amount ?? it.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="space-y-1.5 text-sm max-w-[240px] ml-auto">
            {inv.subtotal != null && (
              <div className="flex justify-between text-[#757675]"><span>Subtotal</span><span>{money(inv.subtotal)}</span></div>
            )}
            {inv.tax ? (
              <div className="flex justify-between text-[#757675]"><span>VAT</span><span>{money(inv.tax)}</span></div>
            ) : null}
            <div className="flex justify-between text-[#1A1B1A] font-medium pt-1.5 border-t border-[#1A1B1A]/[0.06]">
              <span>Total</span><span data-testid="public-invoice-total">{money(inv.total)}</span>
            </div>
            <div className="flex justify-between text-[#205B5A]">
              <span>{outstanding <= 0 ? "Paid in full" : "Outstanding"}</span>
              <span>{outstanding <= 0 ? money(inv.total) : money(outstanding)}</span>
            </div>
          </div>
          {inv.notes && <p className="text-xs text-[#757675] mt-8 pt-4 border-t border-[#1A1B1A]/[0.06]">{inv.notes}</p>}
        </div>
      ) : (
        <div data-testid="public-note-body">
          <p className="text-xs text-[#757675] mb-1">Seen by {note.practitioner_name}</p>
          {note.body_regions?.length > 0 && (
            <p className="text-xs text-[#757675] mb-1">Areas treated: {note.body_regions.join(", ")}</p>
          )}
          {note.status === "draft" && (
            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-block mb-4">
              This record is still a draft and may be updated by your clinician.
            </p>
          )}
          <div className="mb-4" />
          {(note.sections || []).length > 0 ? (
            (note.sections || []).map((sec, i) => (
              <div key={i} className="mb-5">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-1.5">{sec.label}</h3>
                <p className="text-sm text-[#1A1B1A] leading-relaxed whitespace-pre-wrap">{sec.value}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-[#1A1B1A] leading-relaxed whitespace-pre-wrap">
              {note.summary || "Your clinic has shared this record with you. Please contact them if you have any questions."}
            </p>
          )}
        </div>
      )}

      <p className="text-[10px] text-[#9A9B9A] mt-10 pt-5 border-t border-[#1A1B1A]/[0.06]">
        {clinic.name}{clinic.address ? ` · ${clinic.address}` : ""}{clinic.phone ? ` · ${clinic.phone}` : ""}
        {clinic.email ? ` · ${clinic.email}` : ""}
        <br />This link is private to you. Please do not share it.
      </p>
    </Shell>
  );
}
